import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Rating,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import ImageCarousel from '../components/ImageCarousel';
import { ProductService } from '../services/productService';
import { useTranslation } from '../contexts/TranslationContext';
import { translateText } from '../services/translationService';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';
import PromoPopup from '../components/PromoPopup';
import { ListBuilder } from '../components/Admin/ListBuilder';
import FeaturedSection from '../components/FeaturedSection';
import EventFeaturedSection from '../components/EventFeaturedSection';
import CirclesSection from '../components/CirclesSection';
import Counter from '../components/Counter/Counter';
import PageHeroSection from '../components/page-hero-section';
import PageGallerySection from '../components/page-gallery-section';
import PageCtaSection from '../components/page-cta-section';
import PageReviewsSection from '../components/page-reviews-section';
import PageCounterSection from '../components/page-counter-section';
import PageParagraphSection from '../components/page-paragraph-section';
import PageMediaTextSection from '../components/page-media-text-section';
import PageScrollListSection from '../components/page-scroll-list-section';
import PageHeroCarouselSection from '../components/page-hero-carousel-section';
import PageProductsStripSection from '../components/page-products-strip-section';
import PageCardStripSection from '../components/page-card-strip-section';
import PageSideNavigationSection from '../components/page-side-navigation-section';
import NotFoundPage from './NotFoundPage';

// Gallery images for hero section
const heroImages = [
  'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1542272604-787c3835535d?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1594938298605-cd64d190e6bc?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop',
];

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t, language, autoTranslate } = useTranslation();
  const heroRef = React.useRef<HTMLDivElement | null>(null);
  const [failedHeroGallery, setFailedHeroGallery] = React.useState<Record<string, true>>({});
  const HeroBackground = React.memo(({ src, onError }: { src: string; onError?: () => void }) => (
    <Box
      component="img"
      src={src}
      alt="Hero Background"
      onError={onError}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
      loading="eager"
      decoding="async"
    />
  ));
  const { settings } = useWebsiteSettings();
  const heroEnabled = settings?.homepage?.heroEnabled !== false;
  const eventFeatured = settings?.homepage?.eventFeatured as any | undefined;
  const heroMediaPath = settings?.homepage?.mediaPath as string | undefined;
  const heroMediaType = settings?.homepage?.mediaType as ('image' | 'video') | undefined;
  const heroSlideshow = settings?.homepage?.slideshow as string[] | undefined;
  const heroGallery = settings?.homepage?.gallery as string[] | undefined;
  const grid = settings?.homepage?.grid as any | undefined;
  const gridTabs = settings?.homepage?.gridTabs as any[] | undefined;
  const gridTabsDisplay = settings?.homepage?.gridTabsDisplay as any | undefined;
  const globalCounter = settings?.homepage?.counter as any | undefined;
  const counterTabs = settings?.homepage?.counterTabs as any[] | undefined;
  const gridTabsStyle: 'labels' | 'dots' = (typeof gridTabsDisplay?.style === 'string' && ['labels','dots'].includes(gridTabsDisplay.style)) ? gridTabsDisplay.style : 'labels';
  const listTabs = settings?.homepage?.listTabs as any[] | undefined;
  const gridTabsPosition: 'above' | 'below' = (typeof gridTabsDisplay?.position === 'string' && ['above','below'].includes(gridTabsDisplay.position)) ? gridTabsDisplay.position : 'above';
  const gridTabsArrows: boolean = !!gridTabsDisplay?.arrowsEnabled;
  const transitionType: 'none' | 'slide' | 'tiles' | 'cube' = (typeof gridTabsDisplay?.transitionType === 'string' && ['none','slide','tiles','cube'].includes(gridTabsDisplay.transitionType)) ? gridTabsDisplay.transitionType : 'none';
  const [activeGridTab, setActiveGridTab] = React.useState<number>(0);
  const [lastNavDirection, setLastNavDirection] = React.useState<-1 | 0 | 1>(0);
  React.useEffect(() => { setActiveGridTab(0); }, [gridTabs && gridTabs.length]);
  // Animation lifecycle on tab change
  const prevTabRef = React.useRef<number>(0);
  // Sections loaded from /pages/<slug>.json (or homepage.json when no slug)
  const [homeSections, setHomeSections] = React.useState<any[]>([]);
  const [pageJsonMissing, setPageJsonMissing] = React.useState<boolean>(false);
  const { slug } = useParams<{ slug?: string }>();

  const effectiveSlug = React.useMemo(() => {
    const s = String(slug || '').trim().toLowerCase();
    // When on Startseite or /404, optionally reuse another page's content.
    if (s === '' || s === '404') {
      const pages: any[] = Array.isArray((settings as any)?.homepage?.pages) ? (settings as any).homepage.pages : [];
      const url = s === '404' ? '/404' : '/';
      const entry = pages.find((p: any) => p && typeof p === 'object' && String(p.url || '').trim().toLowerCase() === url);
      const df = entry && typeof entry.displayFrom === 'string' ? String(entry.displayFrom).trim().toLowerCase() : '';
      if (df && df !== '/' && df !== '/404') {
        return df.replace(/^\/+/, '');
      }
    }
    return s;
  }, [settings, slug]);

  const usingDisplayFrom = React.useMemo(() => {
    const s = String(slug || '').trim().toLowerCase();
    if (s !== '' && s !== '404') return false;
    const eff = String(effectiveSlug || '').trim().toLowerCase();
    if (s === '' && eff !== '') return true;
    if (s === '404' && eff !== '404') return true;
    return false;
  }, [effectiveSlug, slug]);

  React.useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const file = effectiveSlug ? `/pages/${effectiveSlug}.json` : '/pages/homepage.json';
        const res = await fetch(file, { cache: 'no-store' });
        if (!res.ok) {
          if (!aborted) {
            setPageJsonMissing(true);
            setHomeSections([]);
          }
          return;
        }
        const data = await res.json();
        const content = (data && typeof data === 'object') ? (data as any).content : null;
        const secs = Array.isArray(content?.sections) ? content.sections : [];
        const filtered = secs.filter((s: any) => s && typeof s === 'object' && (s.type === 'grid' || s.type === 'hero' || s.type === 'gallery' || s.type === 'cta' || s.type === 'reviews' || s.type === 'counter' || s.type === 'paragraph' || s.type === 'mediaText' || s.type === 'mediatext' || s.type === 'scrolllist' || s.type === 'heroCarousel' || s.type === 'herocarousel' || s.type === 'products' || s.type === 'cardstrip' || s.type === 'row' || s.type === 'sidenav'));
        if (!aborted) {
          setPageJsonMissing(false);
          setHomeSections(filtered);
        }
      } catch {}
    })();
    return () => { aborted = true; };
  }, [effectiveSlug]);

  const shouldFallback404 = (String(slug || '').trim().toLowerCase() === '404') && !usingDisplayFrom && (pageJsonMissing || homeSections.length === 0);

  const hasPageHero = React.useMemo(() => {
    return Array.isArray(homeSections) && homeSections.some((s: any) => s && typeof s === 'object' && s.type === 'hero' && (s.config?.enabled !== false));
  }, [homeSections]);

  // Local component to render a single grid section using the same visuals/behavior
  const SectionGrid: React.FC<{ section: any; idx: number; listTabsProp: any[]; globalCounterProp: any; counterTabsProp: any[]; }>= ({ section, idx, listTabsProp, globalCounterProp, counterTabsProp }) => {
    const tabs = Array.isArray(section?.gridTabs) ? section.gridTabs : [];
    const hasTabs = tabs.length > 0;
    const display = (section && typeof section.gridTabsDisplay === 'object') ? section.gridTabsDisplay : {};
    const secStyle: 'labels'|'dots' = (typeof display?.style === 'string' && ['labels','dots'].includes(display.style)) ? display.style : gridTabsStyle;
    const secPos: 'above'|'below' = (typeof display?.position === 'string' && ['above','below'].includes(display.position)) ? display.position : gridTabsPosition;
    const secArrows: boolean = display?.arrowsEnabled === true;
    const secTransition: 'none'|'slide'|'tiles'|'cube' = (typeof display?.transitionType === 'string' && ['none','slide','tiles','cube'].includes(display.transitionType)) ? display.transitionType : transitionType;
    const [tabIndex, setTabIndex] = React.useState<number>(0);
    const [dir, setDir] = React.useState<-1|0|1>(0);
    const [entering, setEntering] = React.useState(true);
    const animMs = secTransition === 'tiles' ? 500 : (secTransition === 'slide' ? 450 : (secTransition === 'cube' ? 500 : 0));
    const animKey = `${idx}-${tabIndex}`;
    React.useEffect(() => {
      // Match main homepage grid behavior: toggle entering false then true shortly after
      setEntering(false);
      const id = window.setTimeout(() => setEntering(true), 20);
      return () => window.clearTimeout(id);
    }, [animKey, secTransition]);
    const gsrc: any = hasTabs ? tabs[tabIndex] : section?.grid;
    if (!gsrc) return null;
    const rows = Math.max(1, Number(gsrc?.rows) || 1);
    const cols = Math.max(1, Number(gsrc?.cols) || 1);
    const colWidths = (Array.isArray(gsrc?.colWidths) && gsrc.colWidths.length === cols)
      ? (gsrc.colWidths as any[]).map(n => `${Math.max(0.25, Number(n) || 1)}fr`).join(' ')
      : null;
    const rowHeightPx = Math.max(80, Math.min(480, Number(gsrc?.rowHeight) || 160));
    const total = rows * cols;
    const srcTiles = Array.isArray(gsrc?.tiles) ? gsrc.tiles : [];
    const tiles = Array.from({ length: total }).map((_, i) => srcTiles[i] || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '' });
    const ltabs = Array.isArray(listTabsProp) ? listTabsProp : [];
    return (
      <Container maxWidth="lg" sx={{ py: 8, position: 'relative' }}>
        {hasTabs && secPos === 'above' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {tabs.map((tb: any, i: number) => (
              secStyle === 'labels' ? (
                <Button key={`s${idx}-tab-${i}`} variant={i === tabIndex ? 'contained' : 'outlined'} size="small" onClick={() => { setDir(i > tabIndex ? 1 : -1); setTabIndex(i); }} sx={{ textTransform: 'none' }}>
                  {tb?.label || `Tab ${i+1}`}
                </Button>
              ) : (
                <Box key={`s${idx}-dot-${i}`} component="button" type="button" onClick={() => { setDir(i > tabIndex ? 1 : -1); setTabIndex(i); }} aria-label={(tb?.label || `Tab ${i+1}`)} sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid', borderColor: i === tabIndex ? 'primary.main' : 'divider', bgcolor: i === tabIndex ? 'primary.main' : 'transparent', cursor: 'pointer' }} />
              )
            ))}
          </Box>
        )}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {!!(hasTabs ? tabs[tabIndex]?.title : section?.grid?.title) && (
            <>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {(hasTabs ? tabs[tabIndex]?.title : section?.grid?.title) as any}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Box sx={{ width: 80, height: 4, bgcolor: 'primary.main', borderRadius: 999 }} />
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ position: 'relative', perspective: secTransition === 'cube' ? '1000px' : undefined }}>
          <Box
            key={`grid-anim-sec-${animKey}`}
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: colWidths ? colWidths : `repeat(${cols}, 1fr)` },
              gridAutoRows: `${rowHeightPx}px`,
              transition: secTransition === 'slide' || secTransition === 'cube' ? `transform ${animMs}ms ease-in-out, opacity ${animMs}ms ease-in-out` : undefined,
              transform: secTransition === 'slide'
                ? (entering ? 'translateX(0)' : `translateX(${dir > 0 ? 80 : -80}px)`) 
                : (secTransition === 'cube' ? (entering ? 'rotateY(0deg)' : `rotateY(${dir > 0 ? -35 : 35}deg)`) : undefined),
              transformOrigin: secTransition === 'cube' ? (dir > 0 ? '100% 50%' : '0% 50%') : undefined,
              opacity: (secTransition === 'slide' || secTransition === 'cube') ? (entering ? 1 : 0) : 1,
              willChange: (secTransition === 'slide' || secTransition === 'cube') ? 'transform, opacity' : undefined,
            }}
          >
            {tiles.map((tile: any, tIdx: number) => {
              if (tile && tile.enabled === false) {
                if (tile.reserveSpace) {
                  return (<Card key={tIdx} sx={{ minHeight: rowHeightPx, borderRadius: 3, bgcolor: 'transparent', boxShadow: 'none', gridColumn: `span ${Math.max(1, Number(tile?.colSpan) || 1)}`, gridRow: `span ${Math.max(1, Number((tile as any)?.rowSpan) || 1)}` }} />);
                }
                return null;
              }
              const imgsArr = Array.isArray((tile as any)?.images) ? (tile as any).images.filter((s: any) => typeof s === 'string' && s).map((p: string) => resolvePublicUrl(p)) : [];
              const singleImg = typeof tile?.image === 'string' && tile.image ? resolvePublicUrl(tile.image) : undefined;
              const gallery = imgsArr.length > 0 ? imgsArr : (singleImg ? [singleImg] : []);
              const intervalSec = Math.max(2, Math.min(60, Number((tile as any)?.imageInterval) || 5));
              const img = gallery.length > 0 ? gallery[(Math.floor(Date.now() / (intervalSec * 1000))) % gallery.length] : undefined;
              const bg = tile?.bgColor || (img ? 'transparent' : 'linear-gradient(135deg, #f8fafc, #eef2ff)');
              const color = tile?.textColor || (img ? '#ffffff' : 'text.primary');
              const title = tile?.title || `Kachel ${tIdx + 1}`;
              const subtitle = tile?.subtitle || '';
              const he = typeof tile?.hoverEffect === 'string' ? tile.hoverEffect : 'none';
              const hideHead = tile?.showList === true && (tile as any)?.listFullSize === true && Number.isInteger((tile as any)?.listTabIndex) && (tile as any).listTabIndex >= 0 && (tile as any).listTabIndex < ltabs.length;
              const delayMs = secTransition === 'tiles' ? Math.min(420, (tIdx % cols) * 60 + Math.floor(tIdx / cols) * 80) : 0;
              return (
                <Card key={tIdx} sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', boxShadow: 2, minHeight: rowHeightPx, bgcolor: bg, color, gridColumn: `span ${Math.max(1, Number(tile?.colSpan) || 1)}`, gridRow: `span ${Math.max(1, Number((tile as any)?.rowSpan) || 1)}`, transition: 'transform .3s ease, box-shadow .3s ease, filter .3s ease', ...(he === 'zoom' && { '&:hover': { transform: 'scale(1.02)' } }), ...(he === 'lift' && { '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }), ...(he === 'glow' && { '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.25)' } }), ...(he === 'zoomGlow' && { '&:hover': { transform: 'scale(1.02)', boxShadow: '0 14px 36px rgba(0,0,0,0.28)' } }), ...(secTransition === 'tiles' && { opacity: entering ? 1 : 0, transform: entering ? 'none' : 'translateY(18px) scale(0.98)', transition: `opacity ${animMs}ms ease, transform ${animMs}ms ease`, transitionDelay: `${delayMs}ms`, willChange: 'transform, opacity', }), }}>
                  {img && (<Box component="img" src={img} alt={title} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(.9)' }} />)}
                  <Box sx={{ position: 'absolute', inset: 0, background: img ? 'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.55) 100%)' : 'transparent' }} />
                  {tile?.showCounter && tile?.counterFree !== true && (
                    <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }}>
                      {(() => { const idxSel = Number.isInteger((tile as any)?.counterTabIndex) ? (tile as any).counterTabIndex : -1; const src = (Array.isArray(counterTabsProp) && idxSel >= 0 && idxSel < counterTabsProp.length) ? counterTabsProp[idxSel] : globalCounterProp; return (
                        <Counter value={1234} prefix={typeof src?.prefix === 'string' ? src.prefix : ''} suffix={typeof src?.suffix === 'string' ? src.suffix : ''} prefixNewline={!!src?.prefixNewline} suffixNewline={!!src?.suffixNewline} variant={(typeof src?.variant === 'string' && ['classic','radial','glass'].includes(src.variant)) ? (src.variant as any) : 'classic'} bg={typeof src?.bg === 'string' ? src.bg : 'rgba(255,255,255,0.9)'} textColor={typeof src?.textColor === 'string' ? src.textColor : '#111827'} radius={Math.max(0, Math.min(28, Number(src?.radius) || 8))} scale={Math.max(0.5, Math.min(3, Number(src?.scale) || 1))} fontFamily={typeof src?.fontFamily === 'string' ? src.fontFamily : undefined} valueFontSize={Math.max(10, Math.min(96, Number(src?.valueFontSize) || 28))} prefixFontSize={Math.max(8, Math.min(48, Number(src?.prefixFontSize) || 12))} suffixFontSize={Math.max(8, Math.min(48, Number(src?.suffixFontSize) || 12))} gradientFrom={typeof src?.gradientFrom === 'string' ? src.gradientFrom : '#60a5fa'} gradientTo={typeof src?.gradientTo === 'string' ? src.gradientTo : '#22c55e'} ringWidth={Math.max(2, Math.min(24, Number(src?.ringWidth) || 10))} shadow={(typeof src?.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(src.shadow)) ? (src.shadow as any) : 'soft'} numberLocale={typeof src?.numberLocale === 'string' ? src.numberLocale : 'de-DE'} /> ); })()}
                    </Box>
                  )}
                  {tile?.showCounter && tile?.counterFree === true && (
                    <Box sx={{ position: 'absolute', left: `${Math.max(0, Math.min(100, Number((tile as any)?.counterX) || 85))}%`, top: `${Math.max(0, Math.min(100, Number((tile as any)?.counterY) || 85))}%`, transform: 'translate(-50%, -50%)', zIndex: 3 }}>
                      <Box sx={{ transform: `scale(${Math.max(0.5, Math.min(3, Number((tile as any)?.counterScale) || 1))})`, transformOrigin: 'center' }}>
                        {(() => { const idxSel = Number.isInteger((tile as any)?.counterTabIndex) ? (tile as any).counterTabIndex : -1; const src = (Array.isArray(counterTabsProp) && idxSel >= 0 && idxSel < counterTabsProp.length) ? counterTabsProp[idxSel] : globalCounterProp; return (
                          <Counter value={1234} prefix={typeof src?.prefix === 'string' ? src.prefix : ''} suffix={typeof src?.suffix === 'string' ? src.suffix : ''} prefixNewline={!!src?.prefixNewline} suffixNewline={!!src?.suffixNewline} variant={(typeof src?.variant === 'string' && ['classic','radial','glass'].includes(src.variant)) ? (src.variant as any) : 'classic'} bg={typeof src?.bg === 'string' ? src.bg : 'rgba(255,255,255,0.9)'} textColor={typeof src?.textColor === 'string' ? src.textColor : '#111827'} radius={Math.max(0, Math.min(28, Number(src?.radius) || 8))} scale={Math.max(0.5, Math.min(3, Number(src?.scale) || 1))} fontFamily={typeof src?.fontFamily === 'string' ? src.fontFamily : undefined} valueFontSize={Math.max(10, Math.min(96, Number(src?.valueFontSize) || 28))} prefixFontSize={Math.max(8, Math.min(48, Number(src?.prefixFontSize) || 12))} suffixFontSize={Math.max(8, Math.min(48, Number(src?.suffixFontSize) || 12))} gradientFrom={typeof src?.gradientFrom === 'string' ? src.gradientFrom : '#60a5fa'} gradientTo={typeof src?.gradientTo === 'string' ? src.gradientTo : '#22c55e'} ringWidth={Math.max(2, Math.min(24, Number(src?.ringWidth) || 10))} shadow={(typeof src?.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(src.shadow)) ? (src.shadow as any) : 'soft'} numberLocale={typeof src?.numberLocale === 'string' ? src.numberLocale : 'de-DE'} /> ); })()}
                      </Box>
                    </Box>
                  )}
                  {/* List embedding within section tiles */}
                  {tile?.showList === true && Number.isInteger((tile as any)?.listTabIndex) && (tile as any).listTabIndex >= 0 && (tile as any).listTabIndex < ltabs.length && (
                    <Box sx={{ position: 'absolute', zIndex: 2, ...(((tile as any)?.listFullSize === true) ? { inset: 8 } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '88%' }) }}>
                      {(() => {
                        const tab: any = ltabs[(tile as any).listTabIndex];
                        const ltTitle = typeof tab?.title === 'string' ? tab.title : '';
                        const ltItems = Array.isArray(tab?.items) ? tab.items : [];
                        const ltSize = Math.max(20, Math.min(160, Number(tab?.itemSize) || 40));
                        const ltIconColor = typeof tab?.iconColor === 'string' ? tab.iconColor : undefined;
                        const ltIconBg = typeof tab?.iconBg === 'string' ? tab.iconBg : undefined;
                        return (
                          <ListBuilder
                            title={ltTitle}
                            items={ltItems}
                            itemSize={ltSize}
                            iconColor={ltIconColor}
                            iconBg={ltIconBg}
                            fullSize={(tile as any)?.listFullSize === true}
                            scrollDir={(tile as any)?.listScrollDir === 'horizontal' ? 'horizontal' : 'vertical'}
                          />
                        );
                      })()}
                    </Box>
                  )}
                  {/* Free-positioned CTA */}
                  {tile?.ctaText && tile?.ctaFree === true && (
                    <Box sx={{ position: 'absolute', left: `${Math.max(0, Math.min(100, Number((tile as any)?.ctaX) || 50))}%`, top: `${Math.max(0, Math.min(100, Number((tile as any)?.ctaY) || 50))}%`, transform: 'translate(-50%, -50%)', zIndex: 3 }}>
                      <Button
                        variant="contained"
                        size="small"
                        href={tile?.ctaUrl || '#'}
                        sx={{
                          bgcolor: (tile as any)?.ctaBtnBg ? (tile as any).ctaBtnBg : (img ? 'rgba(59,130,246,0.95)' : undefined),
                          color: (tile as any)?.ctaBtnTextColor || undefined,
                          borderRadius: 9999,
                          px: 1.5,
                          minWidth: `${Math.max(80, Math.min(400, Number((tile as any)?.ctaWidth) || 140))}px`,
                          fontSize: `${Math.max(10, Math.min(36, Number((tile as any)?.ctaFontSize) || 14))}px`,
                          transform: `scale(${Math.max(0.5, Math.min(3, Number((tile as any)?.ctaScale) || 1))})`,
                          transformOrigin: 'center',
                        }}
                      >
                        {tile.ctaText}
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ position: 'relative', zIndex: 3, display: 'grid', gap: 1, p: 2.5, color: img ? '#fff' : color }}>
                    {!hideHead && (
                      <>
                        <Typography variant="h6" fontWeight="bold" sx={{ textShadow: img ? '0 1px 3px rgba(0,0,0,.55)' : 'none' }}>{title}</Typography>
                        {subtitle ? (<Typography variant="body2" sx={{ opacity: 0.9, textShadow: img ? '0 1px 2px rgba(0,0,0,0.45)' : 'none' }}>{subtitle}</Typography>) : null}
                      </>
                    )}
                    {tile?.ctaText && tile?.ctaFree !== true ? (
                      <Box>
                        <Button variant="contained" size="small" href={tile?.ctaUrl || '#'} sx={{ mt: 0.5, bgcolor: (tile as any)?.ctaBtnBg ? (tile as any).ctaBtnBg : (img ? 'rgba(59,130,246,0.95)' : undefined), color: (tile as any)?.ctaBtnTextColor || undefined, borderRadius: 9999, px: 1.5, minWidth: `${Math.max(80, Math.min(400, Number((tile as any)?.ctaWidth) || 140))}px`, fontSize: `${Math.max(10, Math.min(36, Number((tile as any)?.ctaFontSize) || 14))}px`, transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease', ...(((tile as any)?.ctaHoverEffect) === 'lift' && { '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }), ...(((tile as any)?.ctaHoverEffect) === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }), ...(((tile as any)?.ctaHoverEffect) === 'zoom' && { '&:hover': { transform: 'scale(1.05)' } }), ...(((tile as any)?.ctaHoverEffect) === 'zoomGlow' && { '&:hover': { transform: 'scale(1.05)', boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }), }}>
                          {tile.ctaText}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                </Card>
              );
            })}
          </Box>
          {hasTabs && tabs.length > 1 && secArrows && (
            <>
              <Box component="button" type="button" aria-label="Vorheriger Tab" onClick={() => { setDir(1); setTabIndex((tabIndex - 1 + tabs.length) % tabs.length); }} sx={{ position: 'absolute', left: -24, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.7)', opacity: 0.7, backdropFilter: 'blur(2px)', boxShadow: 2, cursor: 'pointer', display: 'grid', placeItems: 'center', transition: 'transform .15s ease, opacity .15s ease, background-color .15s ease', zIndex: 2, '&:hover': { transform: 'translateY(-50%) scale(1.06)', opacity: 1, bgcolor: 'rgba(255,255,255,0.95)' } }}>
                <Box component="svg" viewBox="0 0 24 24" sx={{ width: 16, height: 16, color: 'text.primary' }}>
                  <path d="M15.5 19.5L8 12l7.5-7.5" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </Box>
              </Box>
              <Box component="button" type="button" aria-label="Nächster Tab" onClick={() => { setDir(-1); setTabIndex((tabIndex + 1) % tabs.length); }} sx={{ position: 'absolute', right: -24, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.7)', opacity: 0.7, backdropFilter: 'blur(2px)', boxShadow: 2, cursor: 'pointer', display: 'grid', placeItems: 'center', transition: 'transform .15s ease, opacity .15s ease, background-color .15s ease', zIndex: 2, '&:hover': { transform: 'translateY(-50%) scale(1.06)', opacity: 1, bgcolor: 'rgba(255,255,255,0.95)' } }}>
                <Box component="svg" viewBox="0 0 24 24" sx={{ width: 16, height: 16, color: 'text.primary' }}>
                  <path d="M8.5 4.5L16 12l-7.5 7.5" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </Box>
              </Box>
            </>
          )}
        </Box>
        {hasTabs && secPos === 'below' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3, flexWrap: 'wrap' }}>
            {tabs.map((tb: any, i: number) => (
              secStyle === 'labels' ? (
                <Button key={`s${idx}-tabb-${i}`} variant={i === tabIndex ? 'contained' : 'outlined'} size="small" onClick={() => { setDir(i > tabIndex ? 1 : -1); setTabIndex(i); }} sx={{ textTransform: 'none' }}>
                  {tb?.label || `Tab ${i+1}`}
                </Button>
              ) : (
                <Box key={`s${idx}-dotb-${i}`} component="button" type="button" onClick={() => { setDir(i > tabIndex ? 1 : -1); setTabIndex(i); }} aria-label={(tb?.label || `Tab ${i+1}`)} sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid', borderColor: i === tabIndex ? 'primary.main' : 'divider', bgcolor: i === tabIndex ? 'primary.main' : 'transparent', cursor: 'pointer' }} />
              )
            ))}
          </Box>
        )}
      </Container>
    );
  };
  const MemoSectionGrid = React.useMemo(() => React.memo(SectionGrid), []);
  const [entering, setEntering] = React.useState<boolean>(true);
  React.useEffect(() => {
    // trigger re-enter animation on change; timeout ensures DOM applies initial state before animating
    setEntering(false);
    const t = setTimeout(() => setEntering(true), 20);
    return () => clearTimeout(t);
  }, [activeGridTab, transitionType]);
  const dir = lastNavDirection !== 0 ? lastNavDirection : (activeGridTab >= prevTabRef.current ? 1 : -1);
  React.useEffect(() => { prevTabRef.current = activeGridTab; }, [activeGridTab]);
  const animMs = transitionType === 'tiles' ? 500 : (transitionType === 'slide' ? 450 : (transitionType === 'cube' ? 500 : 0));
  const [animKey, setAnimKey] = React.useState(0);
  React.useEffect(() => { setAnimKey((k) => k + 1); }, [activeGridTab, transitionType]);
  const resolvePublicUrl = React.useCallback((p?: string) => {
    if (!p) return undefined;
    if (/^https?:\/\//i.test(p)) return p;
    try {
      const port = window.location.port;
      const isDevDifferentOrigin = port && port !== '8000';
      if (isDevDifferentOrigin && p.startsWith('/')) {
        return `${window.location.protocol}//${window.location.hostname}:8000${p}`;
      }
    } catch {}
    return p;
  }, []);

  // Load products for Event + Featured section
  const [eventProducts, setEventProducts] = useState<Array<{ id: string; name: string; price: number; originalPrice?: number; image: string }>>([]);
  useEffect(() => {
    const ids = Array.isArray(eventFeatured?.productIds) ? eventFeatured.productIds.filter((x: any) => typeof x === 'string' && x) : [];
    if (eventFeatured?.enabled && ids.length > 0) {
      ProductService.getProductsByIds(ids).then(setEventProducts).catch(() => setEventProducts([]));
    } else {
      setEventProducts([]);
    }
  }, [eventFeatured]);

  // Cycle through hero gallery images if provided in settings
  const [galleryIndex, setGalleryIndex] = useState(0);
  useEffect(() => {
    if (!heroGallery || heroGallery.length === 0) return;
    setGalleryIndex(0);
    const id = window.setInterval(() => {
      setGalleryIndex(prev => (prev + 1) % heroGallery.length);
    }, 4000);
    return () => window.clearInterval(id);
  }, [heroGallery && heroGallery.length]);
  
  // Rotating hero slogans - translated (updates when language changes)
  const heroSlogans = React.useMemo(() => [
    t('home.hero.slogan1'),
    t('home.hero.slogan2'),
    t('home.hero.slogan3'),
    t('home.hero.slogan4'),
    t('home.hero.slogan5'),
  ], [t, language]);
  
  const [sloganIndex, setSloganIndex] = useState(0);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setSloganIndex(prev => (prev + 1) % heroSlogans.length);
    }, 3500);
    return () => window.clearInterval(intervalId);
  }, [heroSlogans.length]);

  // Removed global re-render tick to avoid continuous image reloads

  // Parallax for hero gallery
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const x = useSpring(rawX, { stiffness: 120, damping: 20, mass: 0.3 });
  const y = useSpring(rawY, { stiffness: 120, damping: 20, mass: 0.3 });
  const rotateY = useTransform(x, (v) => v / 3);
  const rotateX = useTransform(y, (v) => -v / 3);

  const handleHeroMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const relX = (e.clientX - cx) / rect.width; // -0.5..0.5
    const relY = (e.clientY - cy) / rect.height; // -0.5..0.5
    const maxShift = 20; // px
    rawX.set(relX * maxShift * 2);
    rawY.set(relY * maxShift * 2);
  };

  const resetHeroSwipe = () => {
    rawX.set(0);
    rawY.set(0);
  };
  
  return shouldFallback404 ? (
    <NotFoundPage />
  ) : (
    <Box>
      {/* Promotion Popup */}
      <PromoPopup promo={settings?.homepage?.promo} resolvePublicUrl={(p) => resolvePublicUrl(p) || ''} />

      {/* Hero Section */}
      {heroEnabled && (
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
          }}
          ref={heroRef}
          onMouseMove={handleHeroMouseMove}
          onMouseLeave={resetHeroSwipe}
        >
          {/* Background Media */}
          <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
          }}
          >
          {heroMediaPath ? (
            heroMediaType === 'image' ? (
              <Box
                component="img"
                src={resolvePublicUrl(heroMediaPath)}
                alt="Hero Background"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                component="video"
                autoPlay
                muted
                loop
                playsInline
                src={resolvePublicUrl(heroMediaPath)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )
          ) : (
            heroGallery && heroGallery.length > 0 ? (
              (() => {
                const effective = heroGallery
                  .map((p) => resolvePublicUrl(p) || '')
                  .filter((p) => !!p && !failedHeroGallery[p]);
                const src = effective.length > 0 ? effective[galleryIndex % effective.length] : '';
                return src ? (
                  <HeroBackground
                    src={src}
                    onError={() => {
                      setFailedHeroGallery((prev) => {
                        if (prev[src]) return prev;
                        return { ...prev, [src]: true };
                      });
                    }}
                  />
                ) : null;
              })()
            ) : (
              <Box
                component="video"
                autoPlay
                muted
                loop
                playsInline
                src="/test.mp4"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )
          )}
        </Box>
        {/* Dark overlay for readability */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.35)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={heroSlogans[sloganIndex]}
                    initial={{ opacity: 0, y: 24, filter: 'blur(6px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -24, filter: 'blur(6px)' }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  >
                    <Typography
                      variant={isMobile ? 'h3' : 'h2'}
                      fontWeight="bold"
                      gutterBottom
                    >
                      {heroSlogans[sloganIndex]}
                    </Typography>
                  </motion.div>
                </AnimatePresence>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{ mb: 4, opacity: 0.9 }}
                >
                  {t('home.hero.subtitle')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/products')}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    {t('home.hero.shopNow')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/register?role=seller')}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    {t('home.hero.startSelling')}
                  </Button>
                </Box>
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                style={{ x, y, rotateX, rotateY, transformPerspective: 800 }}
              >
                <ImageCarousel
                  images={(heroSlideshow && heroSlideshow.length > 0) ? heroSlideshow.map(p => resolvePublicUrl(p) || '') : heroImages}
                  autoPlay={true}
                  autoPlayInterval={4000}
                  showControls={true}
                  showDots={true}
                  height={{ xs: 300, md: 400 }}
                  borderRadius={2}
                  boxShadow={3}
                />
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>
      )}

      {/* Homepage sections from /pages/homepage.json */}
      {Array.isArray(homeSections) && homeSections.length > 0 && (
        <>
          {(() => {
            const renderSection = (sec: any, i: number, keyPrefix: string) => {
              if (sec?.type === 'hero') {
                return (
                  <PageHeroSection
                    key={`${keyPrefix}-hero-${sec?.id || i}`}
                    config={sec?.config}
                    resolvePublicUrl={(p) => resolvePublicUrl(p) || ''}
                  />
                );
              }
              if (sec?.type === 'cta') {
                return (
                  <PageCtaSection
                    key={`${keyPrefix}-cta-${sec?.id || i}`}
                    config={sec?.config}
                    resolvePublicUrl={(p) => resolvePublicUrl(p) || ''}
                  />
                );
              }
              if (sec?.type === 'reviews') {
                return (
                  <PageReviewsSection
                    key={`${keyPrefix}-reviews-${sec?.id || i}`}
                    config={sec?.config}
                    resolvePublicUrl={(p) => resolvePublicUrl(p) || ''}
                  />
                );
              }
              if (sec?.type === 'counter') {
                return (
                  <PageCounterSection
                    key={`${keyPrefix}-counter-${sec?.id || i}`}
                    config={sec?.config}
                  />
                );
              }
              if (sec?.type === 'paragraph') {
                return (
                  <PageParagraphSection
                    key={`${keyPrefix}-paragraph-${sec?.id || i}`}
                    config={sec?.config}
                  />
                );
              }
              if (sec?.type === 'mediaText' || sec?.type === 'mediatext') {
                return (
                  <PageMediaTextSection
                    key={`${keyPrefix}-media-text-${sec?.id || i}`}
                    config={sec?.config}
                    resolvePublicUrl={(p) => resolvePublicUrl(p) || ''}
                  />
                );
              }
              if (sec?.type === 'scrolllist') {
                return (
                  <PageScrollListSection
                    key={`${keyPrefix}-scroll-list-${sec?.id || i}`}
                    config={sec?.config}
                    resolvePublicUrl={(p) => resolvePublicUrl(p) || ''}
                  />
                );
              }
              if (sec?.type === 'heroCarousel' || sec?.type === 'herocarousel') {
                return (
                  <PageHeroCarouselSection
                    key={`${keyPrefix}-hero-carousel-${sec?.id || i}`}
                    config={sec?.config}
                    resolvePublicUrl={(p) => resolvePublicUrl(p) || ''}
                  />
                );
              }
              if (sec?.type === 'products') {
                return (
                  <PageProductsStripSection
                    key={`${keyPrefix}-products-${sec?.id || i}`}
                    config={sec?.config}
                  />
                );
              }
              if (sec?.type === 'cardstrip') {
                return (
                  <PageCardStripSection
                    key={`${keyPrefix}-cardstrip-${sec?.id || i}`}
                    config={sec?.config}
                  />
                );
              }
              if (sec?.type === 'sidenav') {
                return (
                  <PageSideNavigationSection
                    key={`${keyPrefix}-sidenav-${sec?.id || i}`}
                    config={sec?.config}
                    language={language}
                  />
                );
              }
              if (sec?.type === 'gallery') {
                return (
                  <PageGallerySection
                    key={`${keyPrefix}-gallery-${sec?.id || i}`}
                    config={sec?.config}
                    resolvePublicUrl={(p) => resolvePublicUrl(p) || ''}
                  />
                );
              }
              if (sec?.type === 'grid') {
                return (
                  <MemoSectionGrid
                    key={`${keyPrefix}-grid-${sec?.id || i}`}
                    section={sec}
                    idx={i}
                    listTabsProp={Array.isArray(listTabs) ? listTabs : []}
                    globalCounterProp={globalCounter}
                    counterTabsProp={Array.isArray(counterTabs) ? counterTabs : []}
                  />
                );
              }
              if (sec?.type === 'row') {
                const cols = Array.isArray(sec?.columns) ? sec.columns : [];
                const left = cols[0];
                const right = cols[1];
                return (
                  <Container key={`${keyPrefix}-row-${sec?.id || i}`} maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: { xs: 2.5, md: 3 },
                        alignItems: 'start',
                      }}
                    >
                      {left ? (
                        <Box sx={{ minWidth: 0 }}>{renderSection(left, 0, `${keyPrefix}-row-${sec?.id || i}-left`)}</Box>
                      ) : (
                        <Box />
                      )}
                      {right ? (
                        <Box sx={{ minWidth: 0 }}>{renderSection(right, 1, `${keyPrefix}-row-${sec?.id || i}-right`)}</Box>
                      ) : (
                        <Box />
                      )}
                    </Box>
                  </Container>
                );
              }
              return null;
            };

            return homeSections.map((sec, i) => {
              return renderSection(sec, i, 'home');
            });
          })()}
        </>
      )}

      {/* Featured Section */}
      <FeaturedSection feature={settings?.homepage?.feature} resolvePublicUrl={(p) => resolvePublicUrl(p) || ''} />

      {/* Event + Featured Products Section */}
      <EventFeaturedSection settings={eventFeatured} products={eventProducts} />

      {/* Circles Section */}
      <CirclesSection circles={settings?.homepage?.circles as any} resolvePublicUrl={(p) => resolvePublicUrl(p) || ''} />

      {/* Grid Section (with optional tabs) */}
      {(Array.isArray(gridTabs) && gridTabs.length > 0 ? (gridTabs[activeGridTab]?.enabled !== false) : (grid?.enabled)) && (
        <Container maxWidth="lg" sx={{ py: 8, position: 'relative' }}>
          {/* Tabs bar (optional position above) */}
          {Array.isArray(gridTabs) && gridTabs.length > 0 && gridTabsPosition === 'above' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {gridTabs.map((tb, i) => (
                gridTabsStyle === 'labels' ? (
                  <Button key={`htab-${i}`} variant={i === activeGridTab ? 'contained' : 'outlined'} size="small" onClick={() => { setLastNavDirection(i > activeGridTab ? 1 : -1); setActiveGridTab(i); }} sx={{ textTransform: 'none' }}>
                    {tb?.label || `Tab ${i+1}`}
                  </Button>
                ) : (
                  <Box
                    key={`htabdot-${i}`}
                    component="button"
                    type="button"
                    onClick={() => { setLastNavDirection(i > activeGridTab ? 1 : -1); setActiveGridTab(i); }}
                    aria-label={(tb?.label || `Tab ${i+1}`)}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: i === activeGridTab ? 'primary.main' : 'divider',
                      bgcolor: i === activeGridTab ? 'primary.main' : 'transparent',
                      cursor: 'pointer',
                      display: 'inline-block',
                      p: 0,
                      m: 0,
                      outline: 'none',
                      transition: 'transform .15s ease, background-color .15s ease, border-color .15s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}
                  />
                )
              ))}
            </Box>
          )}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {!!(Array.isArray(gridTabs) && gridTabs.length > 0 ? gridTabs[activeGridTab]?.title : grid?.title) && (
              <>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {(Array.isArray(gridTabs) && gridTabs.length > 0 ? gridTabs[activeGridTab]?.title : grid?.title) as any}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Box sx={{ width: 80, height: 4, bgcolor: 'primary.main', borderRadius: 999 }} />
                </Box>
              </>
            )}
          </Box>
          {(() => {
            const gsrc: any = (Array.isArray(gridTabs) && gridTabs.length > 0) ? gridTabs[activeGridTab] : grid;
            const rows = Math.max(1, Number(gsrc?.rows) || 1);
            const cols = Math.max(1, Number(gsrc?.cols) || 1);
            const colWidths = (Array.isArray(gsrc?.colWidths) && gsrc.colWidths.length === cols)
              ? (gsrc.colWidths as any[]).map(n => `${Math.max(0.25, Number(n) || 1)}fr`).join(' ')
              : null;
            const rowHeightPx = Math.max(80, Math.min(480, Number(gsrc?.rowHeight) || 160));
            const total = rows * cols;
            const srcTiles = Array.isArray(gsrc?.tiles) ? gsrc.tiles : [];
            const tiles = Array.from({ length: total }).map((_, i) => srcTiles[i] || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '' });
            return (
              <Box sx={{ position: 'relative', perspective: transitionType === 'cube' ? '1000px' : undefined }}>
                <Box
                  key={`grid-anim-${animKey}`}
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: colWidths ? colWidths : `repeat(${cols}, 1fr)`,
                    },
                    gridAutoRows: `${rowHeightPx}px`,
                    transition: transitionType === 'slide' || transitionType === 'cube' ? `transform ${animMs}ms ease-in-out, opacity ${animMs}ms ease-in-out` : undefined,
                    transform: transitionType === 'slide'
                      ? (entering ? 'translateX(0)' : `translateX(${dir > 0 ? 80 : -80}px)`) 
                      : (transitionType === 'cube' ? (entering ? 'rotateY(0deg)' : `rotateY(${dir > 0 ? -35 : 35}deg)`) : undefined),
                    transformOrigin: transitionType === 'cube' ? (dir > 0 ? '100% 50%' : '0% 50%') : undefined,
                    opacity: (transitionType === 'slide' || transitionType === 'cube') ? (entering ? 1 : 0) : 1,
                    willChange: (transitionType === 'slide' || transitionType === 'cube') ? 'transform, opacity' : undefined,
                  }}
                >
                  {tiles.map((tile: any, idx: number) => {
                  if (tile && tile.enabled === false) {
                    if (tile.reserveSpace) {
                      return (
                        <Card key={idx} sx={{ minHeight: rowHeightPx, borderRadius: 3, bgcolor: 'transparent', boxShadow: 'none', gridColumn: `span ${Math.max(1, Number(tile?.colSpan) || 1)}`, gridRow: `span ${Math.max(1, Number((tile as any)?.rowSpan) || 1)}` }} />
                      );
                    }
                    return null;
                  }
                  const imgsArr = Array.isArray((tile as any)?.images)
                    ? (tile as any).images.filter((s: any) => typeof s === 'string' && s).map((p: string) => resolvePublicUrl(p))
                    : [];
                  const singleImg = typeof tile?.image === 'string' && tile.image ? resolvePublicUrl(tile.image) : undefined;
                  const gallery = imgsArr.length > 0 ? imgsArr : (singleImg ? [singleImg] : []);
                  const intervalSec = Math.max(2, Math.min(60, Number((tile as any)?.imageInterval) || 5));
                  const img = gallery.length > 0
                    ? gallery[(Math.floor(Date.now() / (intervalSec * 1000))) % gallery.length]
                    : undefined;
                  const bg = tile?.bgColor || (img ? 'transparent' : 'linear-gradient(135deg, #f8fafc, #eef2ff)');
                  const color = tile?.textColor || (img ? '#ffffff' : 'text.primary');
                  const title = tile?.title || `Kachel ${idx + 1}`;
                  const subtitle = tile?.subtitle || '';
                  const he = typeof tile?.hoverEffect === 'string' ? tile.hoverEffect : 'none';
                  const hideHead = tile?.showList === true && (tile as any)?.listFullSize === true && Number.isInteger((tile as any)?.listTabIndex) && (tile as any).listTabIndex >= 0 && Array.isArray(listTabs) && (tile as any).listTabIndex < (listTabs as any[]).length;
                  const delayMs = transitionType === 'tiles' ? Math.min(420, (idx % cols) * 60 + Math.floor(idx / cols) * 80) : 0;
                  return (
                    <Card
                      key={idx}
                      sx={{
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: 2,
                        minHeight: rowHeightPx,
                        bgcolor: bg,
                        color,
                        gridColumn: `span ${Math.max(1, Number(tile?.colSpan) || 1)}`,
                        gridRow: `span ${Math.max(1, Number((tile as any)?.rowSpan) || 1)}`,
                        transition: 'transform .3s ease, box-shadow .3s ease, filter .3s ease',
                        ...(he === 'zoom' && { '&:hover': { transform: 'scale(1.02)' } }),
                        ...(he === 'lift' && { '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }),
                        ...(he === 'glow' && { '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.25)' } }),
                        ...(he === 'zoomGlow' && { '&:hover': { transform: 'scale(1.02)', boxShadow: '0 14px 36px rgba(0,0,0,0.28)' } }),
                        ...(transitionType === 'tiles' && {
                          opacity: entering ? 1 : 0,
                          transform: entering ? 'none' : 'translateY(18px) scale(0.98)',
                          transition: `opacity ${animMs}ms ease, transform ${animMs}ms ease`,
                          transitionDelay: `${delayMs}ms`,
                          willChange: 'transform, opacity',
                        }),
                      }}
                    >
                      {img && (
                        <Box component="img" src={img} alt={title} loading="lazy" decoding="async" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(.9)' }} />
                      )}
                      <Box sx={{ position: 'absolute', inset: 0, background: img ? 'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.55) 100%)' : 'transparent' }} />
                      {/* Counter: fixed position (centered) */}
                      {tile?.showCounter && tile?.counterFree !== true && (
                        <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }}>
                          {(() => { const idxSel = Number.isInteger((tile as any)?.counterTabIndex) ? (tile as any).counterTabIndex : -1; const src = (Array.isArray(counterTabs) && idxSel >= 0 && idxSel < counterTabs.length) ? (counterTabs as any[])[idxSel] : globalCounter; return (
                          <Counter
                            value={1234}
                            prefix={typeof src?.prefix === 'string' ? src.prefix : ''}
                            suffix={typeof src?.suffix === 'string' ? src.suffix : ''}
                            prefixNewline={!!src?.prefixNewline}
                            suffixNewline={!!src?.suffixNewline}
                            variant={(typeof src?.variant === 'string' && ['classic','radial','glass'].includes(src.variant)) ? (src.variant as any) : 'classic'}
                            bg={typeof src?.bg === 'string' ? src.bg : 'rgba(255,255,255,0.9)'}
                            textColor={typeof src?.textColor === 'string' ? src.textColor : '#111827'}
                            radius={Math.max(0, Math.min(28, Number(src?.radius) || 8))}
                            scale={Math.max(0.5, Math.min(3, Number(src?.scale) || 1))}
                            fontFamily={typeof src?.fontFamily === 'string' ? src.fontFamily : undefined}
                            valueFontSize={Math.max(10, Math.min(96, Number(src?.valueFontSize) || 28))}
                            prefixFontSize={Math.max(8, Math.min(48, Number(src?.prefixFontSize) || 12))}
                            suffixFontSize={Math.max(8, Math.min(48, Number(src?.suffixFontSize) || 12))}
                            gradientFrom={typeof src?.gradientFrom === 'string' ? src.gradientFrom : '#60a5fa'}
                            gradientTo={typeof src?.gradientTo === 'string' ? src.gradientTo : '#22c55e'}
                            ringWidth={Math.max(2, Math.min(24, Number(src?.ringWidth) || 10))}
                            shadow={(typeof src?.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(src.shadow)) ? (src.shadow as any) : 'soft'}
                            numberLocale={typeof src?.numberLocale === 'string' ? src.numberLocale : 'de-DE'}
                          /> ); })()}
                      </Box>
                  )}
                      {/* Counter: free placement */}
                      {tile?.showCounter && tile?.counterFree === true && (
                        <Box sx={{ position: 'absolute', left: `${Math.max(0, Math.min(100, Number((tile as any)?.counterX) || 85))}%`, top: `${Math.max(0, Math.min(100, Number((tile as any)?.counterY) || 85))}%`, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
                          <Box sx={{ transform: `scale(${Math.max(0.5, Math.min(3, Number((tile as any)?.counterScale) || 1))})`, transformOrigin: 'center' }}>
                            {(() => {
                              const idxSel = Number.isInteger((tile as any)?.counterTabIndex) ? (tile as any).counterTabIndex : -1;
                              const src = (Array.isArray(counterTabs) && idxSel >= 0 && idxSel < counterTabs.length) ? (counterTabs as any[])[idxSel] : globalCounter;
                              return (
                                <Counter
                                  value={1234}
                                  prefix={typeof src?.prefix === 'string' ? src.prefix : ''}
                                  suffix={typeof src?.suffix === 'string' ? src.suffix : ''}
                                  prefixNewline={!!src?.prefixNewline}
                                  suffixNewline={!!src?.suffixNewline}
                                  variant={(typeof src?.variant === 'string' && ['classic','radial','glass'].includes(src.variant)) ? (src.variant as any) : 'classic'}
                                  bg={typeof src?.bg === 'string' ? src.bg : 'rgba(255,255,255,0.9)'}
                                  textColor={typeof src?.textColor === 'string' ? src.textColor : '#111827'}
                                  radius={Math.max(0, Math.min(28, Number(src?.radius) || 8))}
                                  scale={Math.max(0.5, Math.min(3, Number(src?.scale) || 1))}
                                  fontFamily={typeof src?.fontFamily === 'string' ? src.fontFamily : undefined}
                                  valueFontSize={Math.max(10, Math.min(96, Number(src?.valueFontSize) || 28))}
                                  prefixFontSize={Math.max(8, Math.min(48, Number(src?.prefixFontSize) || 12))}
                                  suffixFontSize={Math.max(8, Math.min(48, Number(src?.suffixFontSize) || 12))}
                                  gradientFrom={typeof src?.gradientFrom === 'string' ? src.gradientFrom : '#60a5fa'}
                                  gradientTo={typeof src?.gradientTo === 'string' ? src.gradientTo : '#22c55e'}
                                  ringWidth={Math.max(2, Math.min(24, Number(src?.ringWidth) || 10))}
                                  shadow={(typeof src?.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(src.shadow)) ? (src.shadow as any) : 'soft'}
                                  numberLocale={typeof src?.numberLocale === 'string' ? src.numberLocale : 'de-DE'}
                                />
                              );
                            })()}
                          </Box>
                        </Box>
                      )}
                      {/* List embedding: mimic Admin preview */}
                      {tile?.showList === true && Number.isInteger((tile as any)?.listTabIndex) && (tile as any).listTabIndex >= 0 && Array.isArray(listTabs) && (tile as any).listTabIndex < listTabs.length && (
                        <Box sx={{ position: 'absolute', zIndex: 2, ...( ((tile as any)?.listFullSize === true) ? { inset: 8 } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '88%' }) }}>
                          {(() => {
                            const tab: any = listTabs[(tile as any).listTabIndex];
                            const ltTitle = typeof tab?.title === 'string' ? tab.title : '';
                            const ltItems = Array.isArray(tab?.items) ? tab.items : [];
                            const ltSize = Math.max(20, Math.min(160, Number(tab?.itemSize) || 40));
                            const ltIconColor = typeof tab?.iconColor === 'string' ? tab.iconColor : undefined;
                            const ltIconBg = typeof tab?.iconBg === 'string' ? tab.iconBg : undefined;
                            return (
                              <ListBuilder
                                title={ltTitle}
                                items={ltItems}
                                itemSize={ltSize}
                                iconColor={ltIconColor}
                                iconBg={ltIconBg}
                                fullSize={(tile as any)?.listFullSize === true}
                                scrollDir={(tile as any)?.listScrollDir === 'horizontal' ? 'horizontal' : 'vertical'}
                              />
                            );
                          })()}
                        </Box>
                      )}
                      {/* Free CTA placement */}
                      {tile?.ctaText && tile?.ctaFree === true && (
                        <Box sx={{ position: 'absolute', left: `${Math.max(0, Math.min(100, Number(tile?.ctaX) || 15))}%`, top: `${Math.max(0, Math.min(100, Number(tile?.ctaY) || 80))}%`, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
                          <Button variant="contained" size="small" href={tile?.ctaUrl || '#'} sx={{
                            bgcolor: (tile as any)?.ctaBtnBg ? (tile as any).ctaBtnBg : (img ? 'rgba(59,130,246,0.95)' : undefined),
                            color: (tile as any)?.ctaBtnTextColor || undefined,
                            borderRadius: 9999,
                            px: 1.5,
                            minWidth: `${Math.max(80, Math.min(400, Number((tile as any)?.ctaWidth) || 140))}px`,
                            fontSize: `${Math.max(10, Math.min(36, Number((tile as any)?.ctaFontSize) || 14))}px`,
                            transform: `scale(${Math.max(0.5, Math.min(3, Number((tile as any)?.ctaScale) || 1))})`,
                            transformOrigin: 'center',
                            transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                            ...(((tile as any)?.ctaHoverEffect) === 'lift' && { '&:hover': { transform: `translateY(-2px) scale(${Math.max(0.5, Math.min(3, Number((tile as any)?.ctaScale) || 1))})`, boxShadow: 6 } }),
                            ...(((tile as any)?.ctaHoverEffect) === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }),
                            ...(((tile as any)?.ctaHoverEffect) === 'zoom' && { '&:hover': { transform: `scale(${Math.max(0.5, Math.min(3, Number((tile as any)?.ctaScale) || 1)) * 1.05}` } }),
                            ...(((tile as any)?.ctaHoverEffect) === 'zoomGlow' && { '&:hover': { transform: `scale(${Math.max(0.5, Math.min(3, Number((tile as any)?.ctaScale) || 1)) * 1.05}`, boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }),
                          }}>
                            {tile.ctaText}
                          </Button>
                        </Box>
                      )}

                      <Box sx={{ position: 'relative', zIndex: 3, display: 'grid', gap: 1, p: 2.0, color: img ? '#fff' : color }}>
                        {!hideHead && (
                          <>
                            <Typography variant="h6" fontWeight="bold" sx={{ textShadow: img ? '0 1px 3px rgba(0,0,0,.55)' : 'none' }}>
                              {title}
                            </Typography>
                            {subtitle ? (
                              <Typography variant="body2" sx={{ opacity: 0.9, textShadow: img ? '0 1px 2px rgba(0,0,0,0.45)' : 'none' }}>
                                {subtitle}
                              </Typography>
                            ) : null}
                          </>
                        )}
                        {tile?.ctaText && tile?.ctaFree !== true ? (
                          <Box>
                            <Button variant="contained" size="small" href={tile?.ctaUrl || '#'} sx={{
                              mt: 0.5,
                              bgcolor: (tile as any)?.ctaBtnBg ? (tile as any).ctaBtnBg : (img ? 'rgba(59,130,246,0.95)' : undefined),
                              color: (tile as any)?.ctaBtnTextColor || undefined,
                              borderRadius: 9999,
                              px: 1.5,
                              minWidth: `${Math.max(80, Math.min(400, Number((tile as any)?.ctaWidth) || 140))}px`,
                              fontSize: `${Math.max(10, Math.min(36, Number((tile as any)?.ctaFontSize) || 14))}px`,
                              transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                              ...(((tile as any)?.ctaHoverEffect) === 'lift' && { '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }),
                              ...(((tile as any)?.ctaHoverEffect) === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }),
                              ...(((tile as any)?.ctaHoverEffect) === 'zoom' && { '&:hover': { transform: 'scale(1.05)' } }),
                              ...(((tile as any)?.ctaHoverEffect) === 'zoomGlow' && { '&:hover': { transform: 'scale(1.05)', boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }),
                            }}>
                              {tile.ctaText}
                            </Button>
                          </Box>
                        ) : null}
                      </Box>
                    </Card>
                  );
                })}
                </Box>
                {/* Side handlers (center relative to grid area) */}
                {Array.isArray(gridTabs) && gridTabs.length > 1 && gridTabsArrows && (
                  <>
                    <Box component="button" type="button" aria-label="Vorheriger Tab" onClick={() => { setLastNavDirection(1); setActiveGridTab((activeGridTab - 1 + gridTabs.length) % gridTabs.length); }}
                      sx={{ position: 'absolute', left: -24, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.7)', opacity: 0.7, backdropFilter: 'blur(2px)', boxShadow: 2, cursor: 'pointer', display: 'grid', placeItems: 'center', transition: 'transform .15s ease, opacity .15s ease, background-color .15s ease', zIndex: 2, '&:hover': { transform: 'translateY(-50%) scale(1.06)', opacity: 1, bgcolor: 'rgba(255,255,255,0.95)' } }}>
                      <Box component="svg" viewBox="0 0 24 24" sx={{ width: 16, height: 16, color: 'text.primary' }}>
                        <path d="M15.5 19.5L8 12l7.5-7.5" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </Box>
                    </Box>
                    <Box component="button" type="button" aria-label="Nächster Tab" onClick={() => { setLastNavDirection(-1); setActiveGridTab((activeGridTab + 1) % gridTabs.length); }}
                      sx={{ position: 'absolute', right: -24, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.7)', opacity: 0.7, backdropFilter: 'blur(2px)', boxShadow: 2, cursor: 'pointer', display: 'grid', placeItems: 'center', transition: 'transform .15s ease, opacity .15s ease, background-color .15s ease', zIndex: 2, '&:hover': { transform: 'translateY(-50%) scale(1.06)', opacity: 1, bgcolor: 'rgba(255,255,255,0.95)' } }}>
                      <Box component="svg" viewBox="0 0 24 24" sx={{ width: 16, height: 16, color: 'text.primary' }}>
                        <path d="M8.5 4.5L16 12l-7.5 7.5" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            );
          })()}
          {/* Tabs bar (optional position below) */}
          {Array.isArray(gridTabs) && gridTabs.length > 0 && gridTabsPosition === 'below' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3, flexWrap: 'wrap' }}>
              {gridTabs.map((tb, i) => (
                gridTabsStyle === 'labels' ? (
                  <Button key={`htabb-${i}`} variant={i === activeGridTab ? 'contained' : 'outlined'} size="small" onClick={() => { setLastNavDirection(i > activeGridTab ? 1 : -1); setActiveGridTab(i); }} sx={{ textTransform: 'none' }}>
                    {tb?.label || `Tab ${i+1}`}
                  </Button>
                ) : (
                  <Box
                    key={`htabdotb-${i}`}
                    component="button"
                    type="button"
                    onClick={() => { setLastNavDirection(i > activeGridTab ? 1 : -1); setActiveGridTab(i); }}
                    aria-label={(tb?.label || `Tab ${i+1}`)}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: i === activeGridTab ? 'primary.main' : 'divider',
                      bgcolor: i === activeGridTab ? 'primary.main' : 'transparent',
                      cursor: 'pointer',
                      display: 'inline-block',
                      p: 0,
                      m: 0,
                      outline: 'none',
                      transition: 'transform .15s ease, background-color .15s ease, border-color .15s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}
                  />
                )
              ))}
            </Box>
          )}
        </Container>
      )}


    </Box>
  );
};

export default HomePage;
